/**
 * CrewSettingsView.js
 *
 * Full-screen settings view for Crew module configuration.
 * WBC3 Edition: 4 simple settings
 *
 * Settings:
 * 1. Enabled Positions - Filter irrelevant roles
 * 2. Certificate Warning Days - Default 60, range 14-365
 * 3. Certificate Critical Days - Default 14, range 7-60
 * 4. Show Archived Crew - Toggle visibility
 *
 * Note: HOR Regime setting removed for WBC3 Edition - WBC3 vessels are seagoing only.
 * The underlying hook still supports multiple regimes for future Professional Mode.
 *
 * Pattern: Full-screen overlay accessed via gear icon (same as Checklist Settings)
 */

import React, { useState } from 'react';
import {
  Users,
  Award,
  Archive,
  Info,
  RotateCcw,
  Check,
  AlertTriangle
} from 'lucide-react';
import { useCrewConfig } from '../../../../hooks/domain/crew/useCrewConfig';
// FT-356c: back navigation moved to shell-level MobileBottomBar via
// NavigationBackContext. Header raw ArrowLeft back button removed.
import { useSetBackHandler } from '../../../../contexts/NavigationBackContext';

/**
 * Section wrapper component
 */
const SettingsSection = ({ icon: Icon, title, description, children }) => (
  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
    <div className="bg-gray-900 px-5 py-3">
      <div className="flex items-center gap-3">
        <Icon className="h-5 w-5 text-cyan-400" />
        <div>
          <h3 className="text-base font-semibold text-white">{title}</h3>
          {description && (
            <p className="text-sm text-slate-300 mt-0.5">{description}</p>
          )}
        </div>
      </div>
    </div>
    <div className="p-5">
      {children}
    </div>
  </div>
);

/**
 * Toggle switch component
 */
const Toggle = ({ enabled, onChange, disabled = false }) => (
  <button
    type="button"
    onClick={() => !disabled && onChange(!enabled)}
    disabled={disabled}
    className={`
      relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full
      border-2 border-transparent transition-colors duration-200 ease-in-out
      focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2
      ${enabled ? 'bg-cyan-600' : 'bg-slate-200'}
      ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
    `}
  >
    <span
      className={`
        pointer-events-none inline-block h-5 w-5 transform rounded-full
        bg-white shadow ring-0 transition duration-200 ease-in-out
        ${enabled ? 'translate-x-5' : 'translate-x-0'}
      `}
    />
  </button>
);

/**
 * Position toggle row
 */
const PositionToggleRow = ({ position, enabled, onChange }) => (
  <div className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-slate-50">
    <span className="text-sm text-slate-700">{position}</span>
    <Toggle enabled={enabled} onChange={(val) => onChange(position, val)} />
  </div>
);

/**
 * Number input with range
 */
const NumberInput = ({ value, onChange, min, max, unit = 'days' }) => {
  const [localValue, setLocalValue] = useState(value);

  const handleBlur = () => {
    const clamped = Math.min(max, Math.max(min, localValue));
    setLocalValue(clamped);
    if (clamped !== value) {
      onChange(clamped);
    }
  };

  return (
    <div className="flex items-center gap-3">
      <input
        type="number"
        value={localValue}
        onChange={(e) => setLocalValue(parseInt(e.target.value) || min)}
        onBlur={handleBlur}
        min={min}
        max={max}
        className="w-20 px-3 py-2 text-sm border border-slate-300 rounded-lg
          focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
      />
      <span className="text-sm text-slate-500">{unit}</span>
      <span className="text-xs text-slate-400">({min}-{max})</span>
    </div>
  );
};

/**
 * Main CrewSettingsView component
 */
const CrewSettingsView = ({ onBack }) => {
  // FT-356c: register parent's onBack as the shell-level back handler.
  useSetBackHandler(onBack);

  const {
    allPositionsWithState,
    certificateThresholds,
    showArchivedCrew,
    togglePosition,
    setCertificateWarningDays,
    setCertificateCriticalDays,
    setShowArchivedCrew,
    resetToDefaults,
    lastModified,
    modifiedBy
  } = useCrewConfig();

  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [feedback, setFeedback] = useState(null);

  // Show feedback message
  const showFeedback = (message, type = 'success') => {
    setFeedback({ message, type });
    setTimeout(() => setFeedback(null), 3000);
  };

  // Handle reset
  const handleReset = async () => {
    await resetToDefaults();
    setShowResetConfirm(false);
    showFeedback('Settings reset to defaults');
  };

  // Count enabled positions
  const enabledCount = allPositionsWithState.filter(p => p.enabled).length;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            {/* FT-356c: header raw ArrowLeft back button removed — bottom bar owns Back. */}
            <div className="flex items-center gap-4">
              <div>
                <h1 className="text-xl font-bold text-slate-900">Crew Settings</h1>
                <p className="text-sm text-slate-500">Configure crew module for this vessel</p>
              </div>
            </div>

            <button
              onClick={() => setShowResetConfirm(true)}
              className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600
                hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <RotateCcw className="h-4 w-4" />
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Feedback Toast */}
      {feedback && (
        <div className={`
          fixed top-20 right-4 z-50 px-4 py-3 rounded-lg shadow-lg
          ${feedback.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}
        `}>
          <div className="flex items-center gap-2">
            {feedback.type === 'success' ? (
              <Check className="h-4 w-4" />
            ) : (
              <AlertTriangle className="h-4 w-4" />
            )}
            {feedback.message}
          </div>
        </div>
      )}

      {/* Reset Confirmation */}
      {showResetConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-amber-100 rounded-full">
                <AlertTriangle className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Reset to Defaults?</h3>
                <p className="text-sm text-slate-600 mt-1">
                  This will reset all crew settings to their default values. This action is logged.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowResetConfirm(false)}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800"
              >
                Cancel
              </button>
              <button
                onClick={handleReset}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700"
              >
                Reset Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">

        {/* Audit Notice */}
        <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium">Audit Trail Active</p>
            <p className="mt-0.5">
              All changes are logged with your name and timestamp for SMS compliance.
            </p>
          </div>
        </div>

        {/* Section 1: Enabled Positions */}
        <SettingsSection
          icon={Users}
          title="Enabled Positions"
          description={`${enabledCount} of ${allPositionsWithState.length} positions enabled`}
        >
          <p className="text-sm text-slate-600 mb-4">
            Hide positions that don't apply to your vessel type. Hidden positions won't appear in dropdowns.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-64 overflow-y-auto border border-slate-200 rounded-lg p-2">
            {allPositionsWithState.map(pos => (
              <PositionToggleRow
                key={pos.id}
                position={pos.label}
                enabled={pos.enabled}
                onChange={togglePosition}
              />
            ))}
          </div>
        </SettingsSection>

        {/* Section 2: Certificate Thresholds */}
        <SettingsSection
          icon={Award}
          title="Certificate Warning Thresholds"
          description="When to show expiry warnings for crew certificates"
        >
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Warning Threshold (Amber)
              </label>
              <p className="text-sm text-slate-500 mb-2">
                Show amber warning when certificate expires within this many days
              </p>
              <NumberInput
                value={certificateThresholds.warningDays}
                onChange={setCertificateWarningDays}
                min={14}
                max={365}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Critical Threshold (Red)
              </label>
              <p className="text-sm text-slate-500 mb-2">
                Show red critical warning when certificate expires within this many days
              </p>
              <NumberInput
                value={certificateThresholds.criticalDays}
                onChange={setCertificateCriticalDays}
                min={7}
                max={60}
              />
            </div>
          </div>
        </SettingsSection>

        {/* Section 3: Show Archived Crew */}
        <SettingsSection
          icon={Archive}
          title="Archived Crew Visibility"
          description="Show or hide signed-off crew in lists"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-700">Show Archived Crew</p>
              <p className="text-sm text-slate-500 mt-0.5">
                When enabled, crew members who have signed off will appear in the crew list (greyed out)
              </p>
            </div>
            <Toggle
              enabled={showArchivedCrew}
              onChange={setShowArchivedCrew}
            />
          </div>
        </SettingsSection>

        {/* Last Modified */}
        {lastModified && (
          <div className="text-xs text-slate-500 text-center pt-4">
            Last modified: {new Date(lastModified).toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
            {modifiedBy?.name && ` by ${modifiedBy.name}`}
          </div>
        )}
      </div>
    </div>
  );
};

export default CrewSettingsView;
